import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Calendar, Clock, User, ArrowRight, Search } from 'lucide-react';
import { blogPosts } from '../data/mock';
import { useCurrency } from '../contexts/CurrencyContext';
import SEO from '../components/SEO';

const BlogPage = () => {
  const { formatPrice } = useCurrency();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  const categories = ['All', 'Automation', 'Reviews', 'Lead Generation', 'SMS Marketing', 'Customer Retention', 'Sales'];

  const filteredPosts = blogPosts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || post.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const featuredPost = blogPosts[0];
  const regularPosts = blogPosts.slice(1);

  // Blog List Schema
  const blogListSchema = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "Local Business Heroes Blog",
    "description": "Marketing tips, strategies, and insights for local businesses to grow and scale",
    "url": "https://localbusinessheroes.net/blog",
    "publisher": {
      "@type": "Organization",
      "name": "Local Business Heroes",
      "logo": {
        "@type": "ImageObject",
        "url": "https://customer-assets.emergentagent.com/job_localbizheroes/artifacts/792ds0q9_LBH%20Favicon.png"
      }
    },
    "blogPost": blogPosts.map(post => ({
      "@type": "BlogPosting",
      "headline": post.title,
      "description": post.excerpt,
      "image": post.image,
      "datePublished": post.date,
      "author": {
        "@type": "Person",
        "name": post.author || "Local Business Heroes"
      },
      "url": `https://localbusinessheroes.net/blog/${post.slug}`
    }))
  };

  const breadcrumbs = [
    { name: "Home", url: "/" },
    { name: "Blog", url: "/blog" }
  ];

  return (
    <>
      <SEO 
        title="Marketing Blog for Local Businesses UK | Growth Tips & Strategies"
        description="Practical marketing tips, automation strategies, and proven tactics to help UK local businesses capture more leads, retain customers, and scale profitably."
        keywords="local business marketing UK, small business growth tips, marketing automation, lead generation, customer retention, business scaling UK"
        url="/blog"
        schemaMarkup={blogListSchema}
        breadcrumbs={breadcrumbs}
      />
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#0E163B] to-[#1a2654] text-white py-20 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Grow Smarter —
            <span className="text-[#FF5A3D]"> Marketing Tips for Local Businesses</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-300 mb-8 max-w-4xl mx-auto leading-relaxed">
            Practical advice, proven strategies, and insider tips to help your local business 
            capture more leads, retain customers, and scale profitably.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Button 
              asChild
              className="bg-gradient-to-r from-[#FF5A3D] to-[#FF8C42] hover:from-[#E5512E] hover:to-[#E57A35] text-white font-bold text-lg px-8 py-4 rounded-xl transition-all duration-300 transform hover:scale-105"
            >
              <a href="/pricing">
                Start Implementing Today - {formatPrice(5)} Trial
              </a>
            </Button>
            <Button asChild variant="outline" className="border-white text-white hover:bg-white hover:text-[#0E163B] px-8 py-4 rounded-xl">
              <a href="#latest-posts">Browse Articles</a>
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="text-3xl font-bold text-[#FF5A3D]">{blogPosts.length}</div>
              <div className="text-gray-400">Expert Articles</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-[#FF5A3D]">5-10</div>
              <div className="text-gray-400">Minutes Read Time</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-[#FF5A3D]">100%</div>
              <div className="text-gray-400">Actionable Tips</div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Post */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-[#FF5A3D]/10 text-[#FF5A3D] px-4 py-2 rounded-full mb-6">
              Featured Article
            </Badge>
            <h2 className="text-3xl md:text-5xl font-bold text-[#0E163B] mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Latest Insights
            </h2>
          </div>

          <Card className="overflow-hidden shadow-xl hover:shadow-2xl transition-shadow">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              <div className="relative">
                <img 
                  src={featuredPost.image}
                  alt={featuredPost.title}
                  className="w-full h-64 lg:h-full object-cover"
                />
                <Badge className="absolute top-4 left-4 bg-[#FF5A3D] text-white">
                  {featuredPost.category}
                </Badge>
              </div>
              <div className="p-8 lg:p-12 flex flex-col justify-center">
                <div className="flex items-center space-x-4 text-sm text-gray-500 mb-4">
                  <div className="flex items-center space-x-1">
                    <Calendar size={16} />
                    <span>{new Date(featuredPost.date).toLocaleDateString('en-GB', { 
                      day: 'numeric', 
                      month: 'long', 
                      year: 'numeric' 
                    })}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Clock size={16} />
                    <span>{featuredPost.readTime}</span>
                  </div>
                </div>
                <h3 className="text-2xl lg:text-3xl font-bold text-[#0E163B] mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  {featuredPost.title}
                </h3>
                <p className="text-gray-600 text-lg mb-6 leading-relaxed">
                  {featuredPost.excerpt}
                </p>
                <Button 
                  asChild
                  className="bg-gradient-to-r from-[#FF5A3D] to-[#FF8C42] hover:from-[#E5512E] hover:to-[#E57A35] text-white w-fit"
                >
                  <a href={`/blog/${featuredPost.slug}`}>
                    Read Full Article <ArrowRight size={16} className="ml-2" />
                  </a>
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* Search and Filter */}
      <section id="latest-posts" className="py-10 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-6 items-center justify-between mb-12">
            <div className="relative flex-1 max-w-md">
              <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <Input
                type="text"
                placeholder="Search articles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <Button
                  key={category}
                  variant={selectedCategory === category ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedCategory(category)}
                  className={selectedCategory === category 
                    ? "bg-[#FF5A3D] text-white hover:bg-[#E5512E]" 
                    : "border-[#FF5A3D] text-[#FF5A3D] hover:bg-[#FF5A3D] hover:text-white"
                  }
                >
                  {category}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="pb-20 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          {filteredPosts.length === 0 ? (
            <div className="text-center py-12">
              <h3 className="text-2xl font-semibold text-gray-600 mb-4">No articles found</h3>
              <p className="text-gray-500">Try adjusting your search terms or category filter.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.map((post) => (
                <Card key={post.id} className="overflow-hidden hover:shadow-xl transition-all duration-300 group hover:-translate-y-2">
                  <div className="relative">
                    <img 
                      src={post.image}
                      alt={post.title}
                      className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <Badge className="absolute top-4 left-4 bg-[#FF5A3D] text-white text-xs">
                      {post.category}
                    </Badge>
                  </div>
                  <CardContent className="p-6">
                    <div className="flex items-center space-x-4 text-xs text-gray-500 mb-3">
                      <div className="flex items-center space-x-1">
                        <Calendar size={14} />
                        <span>{new Date(post.date).toLocaleDateString('en-GB')}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Clock size={14} />
                        <span>{post.readTime}</span>
                      </div>
                    </div>
                    <h3 className="text-xl font-bold text-[#0E163B] mb-3 group-hover:text-[#FF5A3D] transition-colors">
                      {post.title}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed mb-4">
                      {post.excerpt}
                    </p>
                    <Button 
                      asChild
                      variant="ghost" 
                      className="text-[#FF5A3D] hover:text-[#E5512E] hover:bg-[#FF5A3D]/10 p-0 h-auto font-semibold"
                    >
                      <a href={`/blog/${post.slug}`}>
                        Read More <ArrowRight size={14} className="ml-1" />
                      </a>
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <Card className="bg-gradient-to-r from-[#0E163B] to-[#1a2654] text-white p-8 lg:p-12 text-center">
            <CardContent className="pt-6">
              <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Never Miss a Growth Tip
              </h2>
              <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
                Get our best marketing strategies, automation tips, and business growth insights 
                delivered straight to your inbox every week.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto mb-6">
                <Input 
                  type="email" 
                  placeholder="Enter your email address" 
                  className="flex-1 bg-white text-gray-900"
                />
                <Button className="bg-gradient-to-r from-[#FF5A3D] to-[#FF8C42] hover:from-[#E5512E] hover:to-[#E57A35] text-white px-8">
                  Subscribe
                </Button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm opacity-90">
                <div>✓ Weekly growth tips</div>
                <div>✓ Exclusive strategies</div>
                <div>✓ No spam, unsubscribe anytime</div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA Section with Direct Action */}
      <section className="py-20 px-4 bg-gradient-to-r from-[#FF5A3D] to-[#FF8C42] text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-5xl font-bold mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Ready to Put These Tips into Action?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Reading about growth is great, but implementing is better. Start using these strategies 
            in your business today with Local Business Heroes.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              asChild
              className="bg-white text-[#FF5A3D] hover:bg-gray-100 font-bold text-lg px-8 py-4 rounded-xl transition-all duration-300 transform hover:scale-105"
            >
              <a href="/pricing">
                Start Your 14-Day Trial for {formatPrice(5)}
              </a>
            </Button>
            <Button asChild variant="outline" className="border-white text-white hover:bg-white hover:text-[#FF5A3D] px-8 py-4 rounded-xl">
              <a href="/features">See All Features</a>
            </Button>
          </div>
          
          <p className="text-sm opacity-75 mt-6">
            Just {formatPrice(5)} to start · Cancel anytime · All the tools mentioned in these articles
          </p>
        </div>
      </section>
    </div>
    </>
  );
};

export default BlogPage;